import { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  TextField, 
  Grid, 
  Typography, 
  FormControl, 
  Select, 
  MenuItem, 
  TableContainer, 
  Table, 
  TableHead, 
  TableRow, 
  TableCell, 
  TableBody, 
  Paper, 
  Card, 
  CardContent, 
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Collapse,
  CircularProgress
} from '@mui/material';
import { toast } from 'react-toastify';
import DeleteIcon from '@mui/icons-material/Delete';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import WarningIcon from '@mui/icons-material/Warning';
import CloseIcon from '@mui/icons-material/Close';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { ncdAPI } from '../../../../services/ncd.api';

dayjs.extend(customParseFormat);

// Basic PHC test categories
const PHC_TEST_CATEGORIES = {
  "Clinical Pathology": [
    { name: "Hemoglobin Estimation (Hb)", warning: true, labTestCode: "clinicalpathology_hemoglobin_estimation_hb" },
    { name: "Total Leukocyte Count (TLC)", warning: true, labTestCode: "clinicalpathology_tlc" },
    { name: "Differential Leukocyte Count (DLC)", warning: true, labTestCode: "clinicalpathology_dlc" },
    { name: "MP (Slide Method)", warning: true, labTestCode: "clinicalpathology_mp_slide_method" },
    { name: "ESR", warning: true, labTestCode: "clinicalpathology_esr" },
    { name: "Prothrombin Time Test and INR (Clotting time)", warning: true, labTestCode: "clinicalpathology_pt_inr" },
    { name: "Blood Group (ABO-RH typing)", warning: true, labTestCode: "clinicalpathology_blood_group_rh_typing" },
    { name: "Platelet count by cell counter", warning: true, labTestCode: "clinicalpathology_platelet_count_by_cell_counter" }
  ],
  "Bio Chemistry": [
    { name: "Random Blood Sugar", warning: true, labTestCode: "biochemistry_random_blood_sugar" },
    { name: "Fasting Blood Sugar", warning: true, labTestCode: "biochemistry_fasting_blood_sugar" },
    { name: "Postprandial Blood Sugar", warning: true, labTestCode: "biochemistry_postprandial_blood_sugar" },
    { name: "HbA1C", warning: true, labTestCode: "biochemistry_hba1c" },
    { name: "Serum Creatinine", warning: true, labTestCode: "biochemistry_s_creatinine" },
    { name: "S. Bilirubin (T)", warning: true, labTestCode: "biochemistry_s_bilirubin_t" },
    { name: "S. Bilirubin (D)", warning: true, labTestCode: "biochemistry_s_bilirubin_d" },
    { name: "Lipid Profile", warning: true, labTestCode: "biochemistry_lipid_profile" }
  ],
  "Stool Analysis": [
    { name: "Stool for OVA and cyst", warning: true, labTestCode: "stoolanalysis_ova_cyst" }
  ],
  "Serology": [
    { name: "Rapid Plasma Reagin (RPR) Kit Test", warning: true, labTestCode: "serology_rpr_rapid_test" },
    { name: "HIV Rapid Test -ELISA", warning: true, labTestCode: "serology_hiv_rapid_test_elisa" },
    { name: "Dengue (Rapid test)", warning: true, labTestCode: "serology_dengue_rapid_test" },
    { name: "Malaria (Rapid test)", warning: true, labTestCode: "serology_malaria_rapid_test" },
    { name: "Sputum for AFB", warning: true, labTestCode: "serology_sputum_afb" }
  ],
  "Urine Analysis": [
    { name: "Urine Sugar And Albumin", warning: true, labTestCode: "urineanalysis_urine_sugar_albumin" },
    { name: "Urine Pregnancy test (UPT)", warning: true, labTestCode: "urineanalysis_urine_pregnancy_test_upt" },
    { name: "Proteinuria", warning: true, labTestCode: "urineanalysis_proteinuria" },
    { name: "Urine Creatinine", warning: true, labTestCode: "urineanalysis_urine_creatinine" }
  ],
  "Radiology": [
    { name: "Chest X-Ray", warning: true, labTestCode: "radiology_chest_x_ray" }
  ],
  "Cardiology": [
    { name: "ECG", warning: true, labTestCode: "cardiology_ecg" }
  ]
};

// Full DH / CHC test categories
const DH_CHC_TEST_CATEGORIES = {
  "Clinical Pathology": [
    { name: "Hemoglobin Estimation (Hb)", warning: true, labTestCode: "clinicalpathology_hemoglobin_estimation_hb" },
    { name: "Total Leukocyte Count (TLC)", warning: true, labTestCode: "clinicalpathology_tlc" },
    { name: "Differential Leukocyte Count (DLC)", warning: true, labTestCode: "clinicalpathology_dlc" },
    { name: "MP (Slide Method)", warning: true, labTestCode: "clinicalpathology_mp_slide_method" },
    { name: "ESR", warning: true, labTestCode: "clinicalpathology_esr" },
    { name: "Prothrombin Time Test and INR (Clotting time)", warning: true, labTestCode: "clinicalpathology_pt_inr" },
    { name: "CBC", warning: true, labTestCode: "clinicalpathology_cbc" },
    { name: "Blood Group (ABO-RH typing)", warning: true, labTestCode: "clinicalpathology_blood_group_rh_typing" },
    { name: "Platelet count by cell counter", warning: true, labTestCode: "clinicalpathology_platelet_count_by_cell_counter" },
    { name: "Packed cell volume (PCV)", warning: true, labTestCode: "clinicalpathology_pcv" }
  ],
  "Radiology": [
    { name: "X-ray without Contrast", warning: true, labTestCode: "radiology_x_ray_without_contrast" },
    { name: "X-ray with Contrast", warning: true, labTestCode: "radiology_x_ray_with_contrast" },
    { name: "Chest X-Ray", warning: true, labTestCode: "radiology_chest_x_ray" },
    { name: "USG", warning: true, labTestCode: "radiology_usg" },
    { name: "Mammogram", warning: true, labTestCode: "radiology_usg_mammogram" }
  ],
  "Bio Chemistry": [
    { name: "Random Blood Sugar", warning: true, labTestCode: "biochemistry_random_blood_sugar" },
    { name: "Fasting Blood Sugar", warning: true, labTestCode: "biochemistry_fasting_blood_sugar" },
    { name: "Postprandial Blood Sugar", warning: true, labTestCode: "biochemistry_postprandial_blood_sugar" },
    { name: "HbA1C", warning: true, labTestCode: "biochemistry_hba1c" },
    { name: "Blood Urea", warning: true, labTestCode: "biochemistry_blood_urea" },
    { name: "Serum Creatinine", warning: true, labTestCode: "biochemistry_s_creatinine" },
    { name: "S. Bilirubin (T)", warning: true, labTestCode: "biochemistry_s_bilirubin_t" },
    { name: "S. Bilirubin (D)", warning: true, labTestCode: "biochemistry_s_bilirubin_d" },
    { name: "SGOT", warning: true, labTestCode: "biochemistry_sgot" },
    { name: "SGPT", warning: true, labTestCode: "biochemistry_sgpt" },
    { name: "S. Alkaline Phosphates", warning: true, labTestCode: "biochemistry_s_alkaline_phosphates" },
    { name: "S. Total Protein", warning: true, labTestCode: "biochemistry_s_total_protein" },
    { name: "S. Albumin", warning: true, labTestCode: "biochemistry_s_albumin" },
    { name: "Lipid Profile", warning: true, labTestCode: "biochemistry_lipid_profile" },
    { name: "S. Amylase", warning: true, labTestCode: "biochemistry_s_amylase" }
  ],
  "Stool Analysis": [
    { name: "Stool for OVA and cyst", warning: true, labTestCode: "stoolanalysis_ova_cyst" }
  ],
  "Serology": [
    { name: "Rapid Plasma Reagin (RPR) Kit Test", warning: true, labTestCode: "serology_rpr_rapid_test" },
    { name: "HIV Rapid Test -ELISA", warning: true, labTestCode: "serology_hiv_rapid_test_elisa" },
    { name: "Dengue (Rapid test)", warning: true, labTestCode: "serology_dengue_rapid_test" },
    { name: "Malaria (Rapid test)", warning: true, labTestCode: "serology_malaria_rapid_test" },
    { name: "Sputum for AFB", warning: true, labTestCode: "serology_sputum_afb" }
  ],
  "Urine Analysis": [
    { name: "Urine Sugar And Albumin", warning: true, labTestCode: "urineanalysis_urine_sugar_albumin" },
    { name: "Urine Complete by Strip", warning: true, labTestCode: "urineanalysis_urine_complete_by_strip" },
    { name: "Urine Pregnancy test (UPT)", warning: true, labTestCode: "urineanalysis_urine_pregnancy_test_upt" },
    { name: "Urine Microscopy", warning: true, labTestCode: "urineanalysis_urine_microscopy" },
    { name: "Proteinuria", warning: true, labTestCode: "urineanalysis_proteinuria" },
    { name: "Urine Creatinine", warning: true, labTestCode: "urineanalysis_urine_creatinine" }
  ],
  "Cardiology": [
    { name: "ECG", warning: true, labTestCode: "cardiology_ecg" }
  ],
  "Other": [
    { name: "Breast Biopsy", warning: true, labTestCode: "other_breast_biopsy" },
    { name: "Pap smear - Cytology based test", warning: true, labTestCode: "other_pap_smear_cytology_test" },
    { name: "Pap smear - HPV based test", warning: true, labTestCode: "other_pap_smear_hpv_test" },
    { name: "Cervical Biopsy", warning: true, labTestCode: "other_cervical_biopsy" },
    { name: "FNAC", warning: true, labTestCode: "other_fnac" },
    { name: "Colposcopy", warning: true, labTestCode: "other_colposcopy" }
  ]
};

// Child parameter definitions for multi-parameter tests in DH/CHC
const CHILD_PARAMETER_DEFINITIONS = {
  "Differential Leukocyte Count (DLC)": [
    { id: 49, name: "Neutrophils", unit: "%", range: "40 - 75 %", type: "numeric" },
    { id: 50, name: "Lymphocytes", unit: "%", range: "20 - 45 %", type: "numeric" },
    { id: 51, name: "Monocytes", unit: "%", range: "2 - 8 %", type: "numeric" },
    { id: 52, name: "Eosinophils", unit: "%", range: "1 - 6 %", type: "numeric" },
    { id: 53, name: "Basophils", unit: "%", range: "0 - 1 %", type: "numeric" }
  ],
  "Prothrombin Time Test and INR (Clotting time)": [
    { id: 59, name: "Prothrombin Time (PT)", unit: "seconds", range: "11.0 - 13.5 seconds", type: "numeric" },
    { id: 60, name: "INR", unit: "Not Applicable", range: "0.8 - 1.2", type: "numeric" }
  ],
  "Blood Group (ABO-RH typing)": [
    { id: 67, name: "ABO Blood Group", unit: "Not Applicable", range: "Normal", type: "dropdown", options: ["A", "B", "AB", "O"] },
    { id: 68, name: "Rh Factor", unit: "Not Applicable", range: "Normal", type: "dropdown", options: ["Positive (+)", "Negative (-)"] }
  ],
  "Lipid Profile": [
    { id: 36, name: "S. Total Cholesterol", unit: "mg%", range: "Normal: 125 - 200 mg%", type: "numeric" },
    { id: 37, name: "S. Triglycerides", unit: "mg%", range: "Normal: 25 - 200 mg%", type: "numeric" },
    { id: 38, name: "S.VLDL", unit: "mg%", range: "Normal: 5 - 40 mg%", type: "numeric" },
    { id: 39, name: "S.HDL", unit: "mg%", range: "Normal: 35-80 mg%", type: "numeric" },
    { id: 40, name: "S.LDL", unit: "mg%", range: "Normal: 85 - 130 mg%", type: "numeric" }
  ],
  "HIV Rapid Test -ELISA": [
    { id: 92, name: "HIV 1", unit: "Not Applicable", range: "Non-reactive", type: "dropdown", options: ["Non-reactive", "Reactive"] },
    { id: 93, name: "HIV 2", unit: "Not Applicable", range: "Non-reactive", type: "dropdown", options: ["Non-reactive", "Reactive"] }
  ],
  "Dengue (Rapid test)": [
    { id: 95, name: "Dengue NS1 Ag", unit: "Not Applicable", range: "Negative", type: "dropdown", options: ["Negative", "Positive"] },
    { id: 96, name: "Dengue IgG", unit: "Not Applicable", range: "Negative", type: "dropdown", options: ["Negative", "Positive"] },
    { id: 97, name: "Dengue IgM", unit: "Not Applicable", range: "Negative", type: "dropdown", options: ["Negative", "Positive"] }
  ],
  "Urine Sugar And Albumin": [
    { id: 105, name: "Urine Sugar", unit: "Not Applicable", range: "Nil", type: "dropdown", options: ["Nil", "Trace", "Present (+)", "Present (++)", "Present (+++)", "Present (++++)"] },
    { id: 106, name: "Urine Albumin", unit: "Not Applicable", range: "Nil", type: "dropdown", options: ["Nil", "Trace", "Present (+)", "Present (++)", "Present (+++)", "Present (++++)"] }
  ],
  "Urine Complete by Strip": [
    { id: 108, name: "Specific Gravity", unit: "Not Applicable", range: "1.005 - 1.030", type: "numeric" },
    { id: 109, name: "pH", unit: "Not Applicable", range: "4.6 - 8.0", type: "numeric" },
    { id: 110, name: "Protein", unit: "Not Applicable", range: "Nil", type: "dropdown", options: ["Nil", "Trace", "+", "++", "+++", "++++"] },
    { id: 111, name: "Glucose", unit: "Not Applicable", range: "Nil", type: "dropdown", options: ["Nil", "Trace", "+", "++", "+++", "++++"] },
    { id: 112, name: "Ketones", unit: "Not Applicable", range: "Negative", type: "dropdown", options: ["Negative", "Positive"] }
  ],
  "Urine Microscopy": [
    { id: 116, name: "Pus cells", unit: "/HPF", range: "0 - 5 /HPF", type: "numeric" },
    { id: 117, name: "Epithelial cells", unit: "/HPF", range: "0 - 5 /HPF", type: "numeric" }
  ],
  "CBC": [
    { id: 62, name: "Hemoglobin (Hb)", unit: "g/dL", range: "12.0 - 17.5 g/dL", type: "numeric" },
    { id: 63, name: "Total Leukocyte Count (TLC)", unit: "cells/cumm", range: "4000 - 11000", type: "numeric" },
    { id: 64, name: "Platelet Count", unit: "lakhs/cumm", range: "1.5 - 4.5", type: "numeric" },
    { id: 65, name: "Packed Cell Volume (PCV)", unit: "%", range: "36 - 48%", type: "numeric" }
  ],
  "USG": [
    { id: 80, name: "Abdomen & Pelvis", unit: "Not Applicable", range: "Normal", type: "dropdown", options: ["Normal", "Abnormal"] },
    { id: 81, name: "Liver & Gallbladder", unit: "Not Applicable", range: "Normal", type: "text" },
    { id: 82, name: "Kidneys & Bladder", unit: "Not Applicable", range: "Normal", type: "text" },
    { id: 83, name: "Impression", unit: "Not Applicable", range: "Normal", type: "text" }
  ],
  "Mammogram": [
    { id: 85, name: "Right Breast Findings", unit: "Not Applicable", range: "Normal", type: "text" },
    { id: 86, name: "Left Breast Findings", unit: "Not Applicable", range: "Normal", type: "text" },
    { id: 87, name: "BI-RADS Category", unit: "Not Applicable", range: "BI-RADS 1", type: "dropdown", options: ["BI-RADS 1", "BI-RADS 2", "BI-RADS 3", "BI-RADS 4", "BI-RADS 5"] },
    { id: 88, name: "Impression", unit: "Not Applicable", range: "Normal", type: "text" }
  ],
  "Breast Biopsy": [
    { id: 123, name: "Specimen", unit: "Not Applicable", range: "Normal", type: "text" },
    { id: 124, name: "Histopathology Findings", unit: "Not Applicable", range: "Normal", type: "text" },
    { id: 125, name: "Impression", unit: "Not Applicable", range: "Normal", type: "dropdown", options: ["Normal", "Abnormal"] }
  ],
  "Pap smear - Cytology based test": [
    { id: 127, name: "Specimen Adequacy", unit: "Not Applicable", range: "Satisfactory", type: "text" },
    { id: 128, name: "Categorization", unit: "Not Applicable", range: "Negative for Intraepithelial Lesion", type: "dropdown", options: ["Normal", "NILM", "LSIL", "HSIL", "Abnormal"] },
    { id: 129, name: "Impression", unit: "Not Applicable", range: "Normal", type: "text" }
  ],
  "Pap smear - HPV based test": [
    { id: 131, name: "HPV High Risk Result", unit: "Not Applicable", range: "Negative", type: "dropdown", options: ["Negative", "Positive"] }
  ],
  "Cervical Biopsy": [
    { id: 133, name: "Histopathology Findings", unit: "Not Applicable", range: "Normal", type: "text" },
    { id: 134, name: "Diagnosis", unit: "Not Applicable", range: "Normal", type: "dropdown", options: ["Normal", "CIN 1", "CIN 2", "CIN 3", "Invasive Cancer"] }
  ],
  "FNAC": [
    { id: 136, name: "Site", unit: "Not Applicable", range: "Normal", type: "text" },
    { id: 137, name: "Cytology Findings", unit: "Not Applicable", range: "Normal", type: "text" },
    { id: 138, name: "Impression", unit: "Not Applicable", range: "Normal", type: "dropdown", options: ["Benign", "Malignant", "Inconclusive"] }
  ],
  "Colposcopy": [
    { id: 140, name: "Colposcopy Impression", unit: "Not Applicable", range: "Normal", type: "dropdown", options: ["Normal", "Abnormal", "Suspicious for Invasion"] }
  ],
  "ECG": [
    { id: 43, name: "ECG", unit: "Not Applicable", range: "Normal: Normal or Abnormal", type: "dropdown", options: ["Normal", "Abnormal"] }
  ],
  "Platelet count by cell counter": [
    { id: 70, name: "Platelet count by cell counter", unit: "Lakhs/cumm", range: "Normal: (1.3 - 4.5 Lakhs/cumm)", type: "numeric" }
  ]
};

const FALLBACK_TEST_IDS = {
  "Hemoglobin Estimation (Hb)": { id: 44, parentId: 0, childIds: [45] },
  "Total Leukocyte Count (TLC)": { id: 46, parentId: 0, childIds: [47] },
  "Differential Leukocyte Count (DLC)": { id: 48, parentId: 0, childIds: [49, 50, 51, 52, 53] },
  "MP (Slide Method)": { id: 54, parentId: 0, childIds: [55] },
  "ESR": { id: 56, parentId: 0, childIds: [57] },
  "Prothrombin Time Test and INR (Clotting time)": { id: 58, parentId: 0, childIds: [59, 60] },
  "CBC": { id: 61, parentId: 0, childIds: [62, 63, 64, 65] },
  "Blood Group (ABO-RH typing)": { id: 66, parentId: 0, childIds: [67, 68] },
  "Platelet count by cell counter": { id: 69, parentId: 0, childIds: [70] },
  "Packed cell volume (PCV)": { id: 71, parentId: 0, childIds: [72] },

  "X-ray without Contrast": { id: 73, parentId: 0, childIds: [74] },
  "X-ray with Contrast": { id: 75, parentId: 0, childIds: [76] },
  "Chest X-Ray": { id: 77, parentId: 0, childIds: [78] },
  "USG": { id: 79, parentId: 0, childIds: [80, 81, 82, 83] },
  "Mammogram": { id: 84, parentId: 0, childIds: [85, 86, 87, 88] },

  "Random Blood Sugar": { id: 9, parentId: 0, childIds: [10] },
  "Fasting Blood Sugar": { id: 11, parentId: 0, childIds: [12] },
  "Postprandial Blood Sugar": { id: 13, parentId: 0, childIds: [14] },
  "HbA1C": { id: 15, parentId: 0, childIds: [16] },
  "Blood Urea": { id: 17, parentId: 0, childIds: [18] },
  "Serum Creatinine": { id: 19, parentId: 0, childIds: [20] },
  "S. Bilirubin (T)": { id: 21, parentId: 0, childIds: [22] },
  "S. Bilirubin (D)": { id: 23, parentId: 0, childIds: [24] },
  "SGOT": { id: 25, parentId: 0, childIds: [26] },
  "SGPT": { id: 27, parentId: 0, childIds: [28] },
  "S. Alkaline Phosphates": { id: 29, parentId: 0, childIds: [30] },
  "S. Total Protein": { id: 31, parentId: 0, childIds: [32] },
  "S. Albumin": { id: 33, parentId: 0, childIds: [34] },
  "Lipid Profile": { id: 35, parentId: 0, childIds: [36, 37, 38, 39] },
  "S. Amylase": { id: 40, parentId: 0, childIds: [41] },

  "Stool for OVA and cyst": { id: 102, parentId: 0, childIds: [103] },

  "Rapid Plasma Reagin (RPR) Kit Test": { id: 89, parentId: 0, childIds: [90] },
  "HIV Rapid Test -ELISA": { id: 91, parentId: 0, childIds: [92, 93] },
  "Dengue (Rapid test)": { id: 94, parentId: 0, childIds: [95, 96, 97] },
  "Malaria (Rapid test)": { id: 98, parentId: 0, childIds: [99] },
  "Sputum for AFB": { id: 100, parentId: 0, childIds: [101] },

  "Urine Sugar And Albumin": { id: 104, parentId: 0, childIds: [105, 106] },
  "Urine Complete by Strip": { id: 107, parentId: 0, childIds: [108, 109, 110, 111, 112] },
  "Urine Pregnancy test (UPT)": { id: 113, parentId: 0, childIds: [114] },
  "Urine Microscopy": { id: 115, parentId: 0, childIds: [116, 117] },
  "Proteinuria": { id: 118, parentId: 0, childIds: [119] },
  "Urine Creatinine": { id: 120, parentId: 0, childIds: [121] },

  "ECG": { id: 42, parentId: 0, childIds: [43] },

  "Breast Biopsy": { id: 122, parentId: 0, childIds: [123, 124, 125] },
  "Pap smear - Cytology based test": { id: 126, parentId: 0, childIds: [127, 128, 129] },
  "Pap smear - HPV based test": { id: 130, parentId: 0, childIds: [131] },
  "Cervical Biopsy": { id: 132, parentId: 0, childIds: [133, 134] },
  "FNAC": { id: 135, parentId: 0, childIds: [136, 137, 138] },
  "Colposcopy": { id: 139, parentId: 0, childIds: [140] }
};

// Default values and ranges based on test name
const TEST_METADATA = {
  "Sputum for AFB": { unit: "Not Applicable", range: "Normal: Negative or positive", type: "dropdown", options: ["Negative", "Positive"] },
  "HbA1C": { unit: "%", range: "Normal: Below 6.0% - Normal Value, 6.0% - 7.0% - Good Control, 7.0% - 8.0% - Fair Control, 8.0% - 10% - Unsatisfactory Control, Above 10% - Poor Control", type: "numeric" },
  "Chest X-Ray": { unit: "Not Applicable", range: "Normal", type: "dropdown", options: ["Normal", "Abnormal"] },
  "Hemoglobin Estimation (Hb)": { unit: "g/dL", range: "Normal: 12.0 - 17.5 g/dL", type: "numeric" },
  "Random Blood Sugar": { unit: "mg/dl", range: "Normal: < 140 mg/dl", type: "numeric" },
  "ECG": { unit: "Not Applicable", range: "Normal", type: "dropdown", options: ["Normal", "Abnormal"] },
  "MP (Slide Method)": { unit: "Not Applicable", range: "Normal: Negative", type: "dropdown", options: ["Negative", "Positive"] },
  "Total Leukocyte Count (TLC)": { unit: "cells/cumm", range: "Normal: 4000 - 11000", type: "numeric" },
  "Differential Leukocyte Count (DLC)": { unit: "%", range: "Normal", type: "numeric" },
  "ESR": { unit: "mm/hr", range: "Normal: < 20 mm/hr", type: "numeric" },
  "Prothrombin Time Test and INR (Clotting time)": { unit: "seconds", range: "Normal: 1.0", type: "numeric" },
  "Blood Group (ABO-RH typing)": { unit: "Not Applicable", range: "Normal", type: "text" },
  "Platelet count by cell counter": { unit: "lakhs/cumm", range: "Normal: 1.5 - 4.5", type: "numeric" },
  "Fasting Blood Sugar": { unit: "mg/dl", range: "Normal: 70 - 100 mg/dl", type: "numeric" },
  "Postprandial Blood Sugar": { unit: "mg/dl", range: "Normal: < 140 mg/dl", type: "numeric" },
  "Serum Creatinine": { unit: "mg/dl", range: "Normal: 0.6 - 1.2 mg/dl", type: "numeric" },
  "S. Bilirubin (T)": { unit: "mg/dl", range: "Normal: 0.1 - 1.2 mg/dl", type: "numeric" },
  "S. Bilirubin (D)": { unit: "mg/dl", range: "Normal: < 0.3 mg/dl", type: "numeric" },
  "Lipid Profile": { unit: "mg%", range: "Normal: 125 - 200 mg%", type: "numeric" },
  "Stool for OVA and cyst": { unit: "Not Applicable", range: "Normal: Negative", type: "dropdown", options: ["Negative", "Positive"] },
  "Rapid Plasma Reagin (RPR) Kit Test": { unit: "Not Applicable", range: "Normal: Non-reactive", type: "dropdown", options: ["Non-reactive", "Reactive"] },
  "HIV Rapid Test -ELISA": { unit: "Not Applicable", range: "Normal: Non-reactive", type: "dropdown", options: ["Non-reactive", "Reactive"] },
  "Dengue (Rapid test)": { unit: "Not Applicable", range: "Normal: Negative", type: "dropdown", options: ["Negative", "Positive"] },
  "Malaria (Rapid test)": { unit: "Not Applicable", range: "Normal: Negative", type: "dropdown", options: ["Negative", "Positive"] },
  "Urine Sugar And Albumin": { unit: "Not Applicable", range: "Normal: Nil", type: "dropdown", options: ["Nil", "Present"] },
  "Urine Pregnancy test (UPT)": { unit: "Not Applicable", range: "Normal: Negative", type: "dropdown", options: ["Negative", "Positive"] },
  "Proteinuria": { unit: "Not Applicable", range: "Normal: Negative", type: "dropdown", options: ["Negative", "Positive"] },
  "Urine Creatinine": { unit: "mg/dl", range: "Normal: 500 - 2000 mg/day", type: "numeric" },

  "CBC": { unit: "Not Applicable", range: "Normal", type: "text" },
  "Packed cell volume (PCV)": { unit: "%", range: "Normal: 36 - 48%", type: "numeric" },
  "X-ray without Contrast": { unit: "Not Applicable", range: "Normal", type: "dropdown", options: ["Normal", "Abnormal"] },
  "X-ray with Contrast": { unit: "Not Applicable", range: "Normal", type: "dropdown", options: ["Normal", "Abnormal"] },
  "USG": { unit: "Not Applicable", range: "Normal", type: "dropdown", options: ["Normal", "Abnormal"] },
  "Mammogram": { unit: "Not Applicable", range: "Normal", type: "dropdown", options: ["Normal", "Abnormal"] },
  "Blood Urea": { unit: "mg/dl", range: "Normal: 15 - 45 mg/dl", type: "numeric" },
  "SGOT": { unit: "U/L", range: "Normal: 5 - 40 U/L", type: "numeric" },
  "SGPT": { unit: "U/L", range: "Normal: 7 - 56 U/L", type: "numeric" },
  "S. Alkaline Phosphates": { unit: "U/L", range: "Normal: 44 - 147 U/L", type: "numeric" },
  "S. Total Protein": { unit: "g/dl", range: "Normal: 6.0 - 8.3 g/dl", type: "numeric" },
  "S. Albumin": { unit: "g/dl", range: "Normal: 3.4 - 5.4 g/dl", type: "numeric" },
  "S. Amylase": { unit: "U/L", range: "Normal: 30 - 110 U/L", type: "numeric" },
  "Urine Complete by Strip": { unit: "Not Applicable", range: "Normal", type: "text" },
  "Urine Microscopy": { unit: "Not Applicable", range: "Normal", type: "text" },
  "Breast Biopsy": { unit: "Not Applicable", range: "Normal", type: "dropdown", options: ["Normal", "Abnormal"] },
  "Pap smear - Cytology based test": { unit: "Not Applicable", range: "Normal: Negative for Intraepithelial Lesion", type: "dropdown", options: ["Normal", "Abnormal", "NILM", "LSIL", "HSIL"] },
  "Pap smear - HPV based test": { unit: "Not Applicable", range: "Normal: Negative", type: "dropdown", options: ["Negative", "Positive"] },
  "Cervical Biopsy": { unit: "Not Applicable", range: "Normal", type: "dropdown", options: ["Normal", "CIN 1", "CIN 2", "CIN 3", "Invasive Cancer"] },
  "FNAC": { unit: "Not Applicable", range: "Normal", type: "dropdown", options: ["Benign", "Malignant", "Inconclusive"] },
  "Colposcopy": { unit: "Not Applicable", range: "Normal", type: "dropdown", options: ["Normal", "Abnormal", "Suspicious for Invasion"] }
};

const formatReferenceRange = (range) => {
  if (!range || typeof range !== 'string') return range || "Normal";
  if (range.includes(',')) {
    return range.split(',').map(s => s.trim()).filter(Boolean).join('\n');
  }
  return range;
};

export const Investigation = ({ individualId, setShowInvestigation, isDhOrChc, isDhChc: isDhChcProp }) => {
  const loggedData = JSON.parse(localStorage.getItem("loggedinUserdetails")) || JSON.parse(localStorage.getItem("NCD_loggedinUserdetails")) || {};
  const facilityType = loggedData.facilityType || (loggedData.facilityTypeId === 3400 ? "DH" : loggedData.facilityTypeId === 3300 ? "CHC" : "PHC");
  const isDhChc = Boolean(isDhOrChc || isDhChcProp || facilityType === "DH" || facilityType === "CHC" || loggedData.facilityTypeId === 3400 || loggedData.facilityTypeId === 3300);

  const categoriesToRender = isDhChc ? DH_CHC_TEST_CATEGORIES : PHC_TEST_CATEGORIES;

  const today = dayjs().format('YYYY-MM-DD');
  
  // Date at top-right
  const [sessionDate, setSessionDate] = useState(today);
  
  // Dialog status
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [selectedTests, setSelectedTests] = useState([]);
  const [warningChecked, setWarningChecked] = useState(false);

  // Raw API lists
  const [rawOrderedList, setRawOrderedList] = useState([]);
  const [masterList, setMasterList] = useState([]);

  // Ordered Tests list
  const [orderedTests, setOrderedTests] = useState([]);
  const [loading, setLoading] = useState(false);

  // Local results and dates
  const [localResults, setLocalResults] = useState({});
  const [localTestDates, setLocalTestDates] = useState({});

  // Expanded editor for results (array of open card keys)
  const [expandedTestIds, setExpandedTestIds] = useState([]);
  
  // Result inputs
  const [tempResults, setTempResults] = useState({});
  const [tempResultDates, setTempResultDates] = useState({});
  const [tempResultValue, setTempResultValue] = useState("");
  const [dateErrors, setDateErrors] = useState({});

  const fetchOrderedTests = async () => {
    if (!individualId) return;
    try {
      const response = await ncdAPI.getInAssOrderedLabTests(individualId);
      const orderedList = response?.data?.orderedLabTestsList || response?.orderedLabTestsList;
      if (orderedList) {
        setRawOrderedList(orderedList);
        
        // Flatten the hierarchy to match UI state representation
        const flatList = [];
        const initialResults = {};
        const initialDates = {};

        const seenKeys = new Set();
        orderedList.forEach(cat => {
          if (cat.childLabReports) {
            cat.childLabReports.forEach(test => {
              if (test.requested) {
                const uniqueKey = test.labTestOrderId || test.labRequestId || (test.id ? `id_${test.id}` : null);
                if (uniqueKey && seenKeys.has(uniqueKey)) return;
                if (uniqueKey) seenKeys.add(uniqueKey);

                const isCompleted = test.labTestStatus === 'Completed';
                const childParams = test.childLabReports || [];
                const firstParamVal = childParams[0]?.data || childParams[0]?.value || childParams[0]?.testValue || "";
                
                const resultDate = childParams[0]?.testDate || test.testDate || test.prescribedDate || "";
                if (resultDate) {
                  initialDates[test.id] = resultDate;
                }

                const itemKey = test.labTestOrderId || test.labRequestId || `order_${test.id}_${flatList.length + 1}`;

                childParams.forEach(param => {
                  const val = param.data !== undefined && param.data !== null ? param.data : (param.value !== undefined && param.value !== null ? param.value : (param.testValue !== undefined && param.testValue !== null ? param.testValue : ""));
                  if (val !== undefined && val !== null && val !== "") {
                    initialResults[`${itemKey}_${param.id}`] = val;
                  }
                });

                flatList.push({
                  uniqueCardKey: itemKey,
                  id: test.id,
                  parentId: test.parentId,
                  reportType: test.reportType,
                  name: test.displayText,
                  orderedDate: test.prescribedDate ? dayjs(test.prescribedDate).format("DD MMM YYYY") : "",
                  orderedBy: test.prescribedBy || "",
                  orderingFacility: test.prescribedCenterName || "",
                  status: isCompleted ? 'Completed' : 'Pending Results',
                  result: firstParamVal,
                  resultDate: resultDate ? dayjs(resultDate).format("DD MMM YYYY") : "",
                  attachedLabTestFiles: test.attachedLabTestFiles || [],
                  childLabReports: childParams,
                  rawTestObject: test,
                  labRequestId: test.labTestOrderId || test.labRequestId || null
                });
              }
            });
          }
        });

        setLocalResults(prev => ({ ...initialResults, ...prev }));
        setLocalTestDates(prev => ({ ...initialDates, ...prev }));
        setOrderedTests(flatList);
      }
    } catch (error) {
      console.error("Failed to fetch ordered lab tests:", error);
      toast.error("Failed to fetch ordered lab tests");
    }
  };

  const fetchMasterList = async () => {
    if (!individualId) return;
    try {
      const response = await ncdAPI.getInAssInvestigation(individualId);
      const dataPayload = response?.data || response;
      const list = dataPayload?.investigationList || dataPayload?.orderedLabTestsList || (Array.isArray(dataPayload) ? dataPayload : []);
      setMasterList(list);
    } catch (error) {
      console.error("Failed to fetch master investigation list:", error);
    }
  };

  useEffect(() => {
    const initData = async () => {
      setLoading(true);
      try {
        await Promise.all([fetchOrderedTests(), fetchMasterList()]);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    if (individualId) {
      initData();
    }
  }, [individualId]);

  const handleClose = () => {
    if (setShowInvestigation) {
      setShowInvestigation(false);
    }
  };

  // Check if any of the selected tests are already pending
  const hasPendingWarning = () => {
    return selectedTests.some(testName => {
      // Find if test with warning flag is in ordered list and still pending
      const testMeta = Object.values(categoriesToRender).flat().find(t => t.name === testName);
      if (testMeta && testMeta.warning) {
        return orderedTests.some(ordered => ordered.name === testName && ordered.status === "Pending Results");
      }
      return false;
    });
  };

  const handleCheckboxToggle = (testName) => {
    setSelectedTests(prev => {
      if (prev.includes(testName)) {
        return prev.filter(t => t !== testName);
      } else {
        return [...prev, testName];
      }
    });
  };

  const handleOrderTests = () => {
    const dateFormatted = dayjs(sessionDate).format("DD MMM YYYY");
    const username = localStorage.getItem("ncd_username");

    const newOrders = [];
    selectedTests.forEach(testName => {
      let matchedTest = null;
      
      // 1. Try case-insensitive match on masterList
      masterList.forEach(cat => {
        const found = cat.childLabReports?.find(t => t.displayText?.toLowerCase() === testName.toLowerCase());
        if (found) matchedTest = found;
      });

      // 2. Try match on FALLBACK_TEST_IDS if not found in masterList
      if (!matchedTest) {
        const fallbackKey = Object.keys(FALLBACK_TEST_IDS).find(k => k.toLowerCase() === testName.toLowerCase());
        const fallbackMeta = fallbackKey ? FALLBACK_TEST_IDS[fallbackKey] : null;
        if (fallbackMeta) {
          const testMeta = Object.values(categoriesToRender).flat().find(t => t.name.toLowerCase() === testName.toLowerCase());
          const childrenDefs = CHILD_PARAMETER_DEFINITIONS[fallbackKey] || [];
          matchedTest = {
            id: fallbackMeta.id,
            parentId: fallbackMeta.parentId || 0,
            reportType: testMeta?.labTestCode || testName.toLowerCase().replace(/\s+/g, '_'),
            displayText: testName,
            childLabReports: childrenDefs.length > 0
              ? childrenDefs.map((cdef, idx) => ({
                  id: cdef.id || (fallbackMeta.childIds?.[idx] || (fallbackMeta.id * 100 + idx + 1)),
                  parentId: fallbackMeta.id,
                  displayText: cdef.name,
                  data: "",
                  value: "",
                  testValue: "",
                  measurementUnit: cdef.unit,
                  helpText: cdef.range,
                  dataType: cdef.type === 'dropdown' ? 'dropdown' : (cdef.type === 'numeric' ? 'numeric' : 'text'),
                  dataTypeValidCriteria: cdef.type === 'dropdown' ? (cdef.options ? cdef.options.join(',') : '') : (cdef.type === 'numeric' ? 'numeric' : 'text')
                }))
              : fallbackMeta.childIds.map(cid => ({
                  id: cid,
                  parentId: fallbackMeta.id,
                  displayText: testName,
                  data: "",
                  value: "",
                  testValue: ""
                }))
          };
        }
      }

      if (matchedTest) {
        newOrders.push({
          id: `new_${Date.now()}_${matchedTest.id}`,
          parentId: matchedTest.parentId,
          reportType: matchedTest.reportType,
          name: matchedTest.displayText,
          orderedDate: dateFormatted,
          orderedBy: username,
          orderingFacility: "",
          status: "Pending Results",
          result: "",
          resultDate: "",
          attachedLabTestFiles: [],
          childLabReports: matchedTest.childLabReports || [],
          rawTestObject: matchedTest,
          labRequestId: null,
          isLocalNewOrder: true
        });
      } else {
        newOrders.push({
          id: `new_${Date.now()}_${testName}`,
          parentId: 0,
          reportType: testName.toLowerCase().replace(/\s+/g, '_'),
          name: testName,
          orderedDate: dateFormatted,
          orderedBy: username,
          orderingFacility: "",
          status: "Pending Results",
          result: "",
          resultDate: "",
          attachedLabTestFiles: [],
          childLabReports: [],
          rawTestObject: null,
          labRequestId: null,
          isLocalNewOrder: true
        });
      }
    });

    setOrderedTests(prev => {
      const filteredNew = newOrders.filter(newTest => 
        !prev.some(existing => 
          existing.name.toLowerCase() === newTest.name.toLowerCase() && 
          existing.status !== 'Completed'
        )
      );
      return [...filteredNew, ...prev];
    });
    toast.success("Test(s) added to the order list locally.");
    
    // Reset modal states
    setShowOrderModal(false);
    setSelectedTests([]);
    setWarningChecked(false);
  };

  const handleDeleteTest = (testKey) => {
    setOrderedTests(prev => prev.filter(t => (t.uniqueCardKey || t.id) !== testKey));
    toast.info("Test order removed locally");
  };

  const getEffectiveChildReports = (test) => {
    if (test.childLabReports && test.childLabReports.length > 0) {
      return test.childLabReports;
    }
    const cleanName = test.name?.trim();
    const childDefs = CHILD_PARAMETER_DEFINITIONS[test.name] || CHILD_PARAMETER_DEFINITIONS[cleanName];
    if (childDefs && childDefs.length > 0) {
      return childDefs.map((cdef, idx) => ({
        id: cdef.id || `${test.id}_p${idx}`,
        parentId: test.id,
        displayText: cdef.name,
        data: "",
        value: "",
        testValue: "",
        measurementUnit: cdef.unit,
        helpText: cdef.range,
        dataType: cdef.type === 'dropdown' ? 'dropdown' : (cdef.type === 'numeric' ? 'numeric' : 'text'),
        dataTypeValidCriteria: cdef.type === 'dropdown' ? (cdef.options ? cdef.options.join(',') : '') : (cdef.type === 'numeric' ? 'numeric' : 'text')
      }));
    }
    const fallbackMeta = FALLBACK_TEST_IDS[test.name] || FALLBACK_TEST_IDS[cleanName];
    if (fallbackMeta && fallbackMeta.childIds && fallbackMeta.childIds.length > 0) {
      return fallbackMeta.childIds.map(cid => ({
        id: cid,
        parentId: fallbackMeta.id || test.id,
        displayText: test.name,
        data: "",
        value: "",
        testValue: ""
      }));
    }
    return [{ id: test.id, parentId: test.parentId || 0, displayText: test.name }];
  };

  const handleOpenEnterResult = (test) => {
    const testKey = test.uniqueCardKey || test.labTestOrderId || test.id;
    const isCurrentlyExpanded = expandedTestIds.includes(testKey);

    if (isCurrentlyExpanded) {
      setExpandedTestIds(prev => prev.filter(id => id !== testKey));
      return;
    }

    setExpandedTestIds(prev => [...prev, testKey]);
    setDateErrors(prev => ({ ...prev, [testKey]: false }));
    
    const localDate = localTestDates[testKey] || test.resultDate;
    setTempResultDates(prev => ({
      ...prev,
      [testKey]: localDate ? dayjs(localDate).format('YYYY-MM-DD') : null
    }));
    
    const initialTemp = {};
    const effectiveChildren = getEffectiveChildReports(test);

    if (effectiveChildren && effectiveChildren.length > 0) {
      effectiveChildren.forEach(param => {
        const paramKey = `${testKey}_${param.id}`;
        const val = localResults[paramKey] !== undefined 
          ? localResults[paramKey] 
          : (param.data !== undefined && param.data !== null ? param.data : (param.value !== undefined && param.value !== null ? param.value : (param.testValue !== undefined && param.testValue !== null ? param.testValue : "")));
        initialTemp[paramKey] = val;
        initialTemp[param.id] = val;
      });
    } else {
      const val = localResults[testKey] !== undefined ? localResults[testKey] : (test.result || "");
      initialTemp[testKey] = val;
    }
    setTempResults(prev => ({
      ...prev,
      ...initialTemp
    }));
    setTempResultValue(test.result || "");
  };

  const handleSaveResult = (testKey) => {
    const test = orderedTests.find(t => (t.uniqueCardKey || t.id) === testKey);
    if (!test) return;

    const cardDate = tempResultDates[testKey];
    if (!cardDate) {
      setDateErrors(prev => ({ ...prev, [testKey]: true }));
      toast.error("Please select a Test Date");
      return;
    }
    setDateErrors(prev => ({ ...prev, [testKey]: false }));

    const updatedLocalResults = {};
    const effectiveChildren = getEffectiveChildReports(test);

    if (effectiveChildren && effectiveChildren.length > 0) {
      effectiveChildren.forEach(param => {
        const paramKey = `${testKey}_${param.id}`;
        const val = tempResults[paramKey] !== undefined ? tempResults[paramKey] : tempResults[param.id];
        if (val !== undefined) {
          updatedLocalResults[paramKey] = val;
        }
      });
    } else {
      updatedLocalResults[testKey] = tempResults[testKey] || tempResultValue;
    }

    const hasValues = Object.values(updatedLocalResults).some(val => val !== undefined && val !== "");
    if (!hasValues) {
      toast.error("Please enter a result value");
      return;
    }

    setLocalResults(prev => ({
      ...prev,
      ...updatedLocalResults
    }));

    setLocalTestDates(prev => ({
      ...prev,
      [testKey]: cardDate
    }));

    const dateFormatted = dayjs(cardDate).format("DD MMM YYYY");
    const firstParamId = effectiveChildren?.[0]?.id || test.id;
    const firstParamKey = `${testKey}_${firstParamId}`;
    const firstParamVal = updatedLocalResults[firstParamKey] !== undefined && updatedLocalResults[firstParamKey] !== "" 
      ? updatedLocalResults[firstParamKey] 
      : (tempResults[firstParamId] || tempResultValue || Object.values(updatedLocalResults).find(v => v !== "") || "");

    setOrderedTests(prev => prev.map(t => {
      if ((t.uniqueCardKey || t.id) === testKey) {
        return {
          ...t,
          status: t.status === "Completed" ? "Completed" : "Pending Results",
          result: firstParamVal,
          resultDate: dateFormatted,
          childLabReports: effectiveChildren.length > 0 ? effectiveChildren.map(param => ({
            ...param,
            data: updatedLocalResults[`${testKey}_${param.id}`] !== undefined ? updatedLocalResults[`${testKey}_${param.id}`] : param.data
          })) : t.childLabReports
        };
      }
      return t;
    }));

    setExpandedTestIds(prev => prev.filter(id => id !== testKey));
    toast.success("Result entered locally. Click SAVE to submit.");
  };

  const handleAttachFile = async (testId, e) => {
    const file = e.target.files[0];
    if (!file) return;

    const test = orderedTests.find(t => t.id === testId);
    const labRequestId = test?.labRequestId || test?.rawTestObject?.labRequestId || test?.rawTestObject?.labTestOrderId || test?.labTestOrderId || null;

    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64String = reader.result.split(",")[1];
        const payload = {
          fileType: "investigation",
          fileName: file.name,
          fileContent: base64String,
          refId: labRequestId
        };

        toast.info("Uploading file...");
        const response = await ncdAPI.submitInAssLabTestFile(individualId, testId, payload);
        if (response) {
          toast.success("File attached successfully!");
          await fetchOrderedTests();
        } else {
          toast.error("Failed to upload file");
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error("File upload error:", error);
      toast.error("Something went wrong while uploading file");
    }
  };

  const handleRemoveAttachedFile = (testId, fileName) => {
    setOrderedTests(prev => prev.map(t => {
      if (t.id === testId) {
        return {
          ...t,
          attachedLabTestFiles: t.attachedLabTestFiles.filter(f => f !== fileName)
        };
      }
      return t;
    }));
    toast.info("Attachment removed locally");
  };

  const handleSaveAll = async () => {
    const enteredResultsList = [];
    const orderedLabTestsListPayload = [];
    const username = localStorage.getItem("ncd_username");

    const getCleanId = (id) => {
      if (typeof id === 'string' && id.startsWith('new_')) {
        const parts = id.split('_');
        const lastPart = parts[parts.length - 1];
        return isNaN(lastPart) ? lastPart : Number(lastPart);
      }
      return isNaN(id) ? id : Number(id);
    };

    orderedTests.forEach(test => {
      const testKey = test.uniqueCardKey || test.labTestOrderId || test.id;
      const testId = test.id;
      const existingOrderId = test.rawTestObject?.labTestOrderId || test.rawTestObject?.labRequestId || test.labRequestId || test.labTestOrderId || null;
      const isNewOrder = !existingOrderId || test.isLocalNewOrder || (typeof testId === 'string' && testId.startsWith('new_'));

      const effectiveChildren = getEffectiveChildReports(test);

      const hasLocalValue = (effectiveChildren && effectiveChildren.length > 0)
        ? effectiveChildren.some(p => {
            const pk = `${testKey}_${p.id}`;
            return (tempResults[pk] !== undefined && tempResults[pk] !== "") || 
                   (tempResults[p.id] !== undefined && tempResults[p.id] !== "") ||
                   (localResults[pk] !== undefined && localResults[pk] !== "") || 
                   (localResults[p.id] !== undefined && localResults[p.id] !== "");
          })
        : ((tempResults[testKey] !== undefined && tempResults[testKey] !== "") || 
           (tempResults[testId] !== undefined && tempResults[testId] !== "") ||
           (localResults[testKey] !== undefined && localResults[testKey] !== "") || 
           (localResults[testId] !== undefined && localResults[testId] !== ""));

      const isCompleted = test.status === 'Completed' || hasLocalValue;
      // New orders MUST have labTestStatus: "Pending" for backend validation
      const status = isNewOrder ? "Pending" : (isCompleted ? "Completed" : "Pending");

      const rawDate = tempResultDates[testKey] || localTestDates[testKey] || localTestDates[testId] || test.resultDate;
      const testDateFormatted = rawDate ? dayjs(rawDate).format("DD MMM YYYY") : (hasLocalValue ? dayjs().format("DD MMM YYYY") : null);

      if (effectiveChildren && effectiveChildren.length > 0) {
        effectiveChildren.forEach(param => {
          const paramKey = `${testKey}_${param.id}`;
          const enteredVal = tempResults[paramKey] !== undefined && tempResults[paramKey] !== ""
            ? tempResults[paramKey]
            : (tempResults[param.id] !== undefined && tempResults[param.id] !== ""
                ? tempResults[param.id]
                : (localResults[paramKey] !== undefined && localResults[paramKey] !== ""
                    ? localResults[paramKey] 
                    : (localResults[param.id] !== undefined && localResults[param.id] !== ""
                        ? localResults[param.id] 
                        : (param.data !== undefined && param.data !== null ? param.data : (param.value !== undefined && param.value !== null ? param.value : (param.testValue !== undefined && param.testValue !== null ? param.testValue : ""))))));

          if (enteredVal !== "" && enteredVal !== undefined && enteredVal !== null) {
            const parsedVal = (!isNaN(enteredVal) && enteredVal !== "" && typeof enteredVal !== 'boolean') ? Number(enteredVal) : String(enteredVal);
            enteredResultsList.push({
              resultId: getCleanId(param.id),
              resultParentId: getCleanId(param.parentId || test.id),
              resultValue: parsedVal,
              labTestOrderId: isNewOrder ? null : existingOrderId
            });
          }
        });
      } else {
        const enteredVal = tempResults[testKey] !== undefined && tempResults[testKey] !== ""
          ? tempResults[testKey]
          : (tempResults[testId] !== undefined && tempResults[testId] !== ""
              ? tempResults[testId]
              : (localResults[testKey] !== undefined && localResults[testKey] !== ""
                  ? localResults[testKey] 
                  : (localResults[testId] !== undefined && localResults[testId] !== ""
                      ? localResults[testId] 
                      : (test.result || ""))));

        if (enteredVal !== "" && enteredVal !== undefined && enteredVal !== null) {
          const parsedVal = (!isNaN(enteredVal) && enteredVal !== "" && typeof enteredVal !== 'boolean') ? Number(enteredVal) : String(enteredVal);
          enteredResultsList.push({
            resultId: getCleanId(testId),
            resultParentId: getCleanId(test.parentId || 0),
            resultValue: parsedVal,
            labTestOrderId: isNewOrder ? null : existingOrderId
          });
        }
      }

      const testMeta = Object.values(categoriesToRender).flat().find(t => t.name === test.name);
      const testCode = testMeta?.labTestCode || test.reportType || "";
      const deletedFiles = test.rawTestObject && test.rawTestObject.deletedLabTestFilesList && test.rawTestObject.deletedLabTestFilesList.length > 0
        ? test.rawTestObject.deletedLabTestFilesList
        : (isNewOrder ? [] : null);

      const orderedItem = {
        labTestOrderId: isNewOrder ? null : existingOrderId,
        labTestCode: testCode,
        labTestId: getCleanId(testId),
        labTestCreatedBy: test.rawTestObject?.prescribedBy || test.orderedBy || username,
        labTestStatus: status,
        deletedLabTestFilesList: isNewOrder ? [] : deletedFiles,
        testDate: isNewOrder ? null : (testDateFormatted)
      };

      const rawRequestedDate = test.rawTestObject?.labTestRequestedDate || test.rawTestObject?.prescribedDate;
      let requestedDateFormatted = null;
      if (rawRequestedDate) {
        const parsed = dayjs(rawRequestedDate);
        requestedDateFormatted = parsed.isValid() ? parsed.format("DD MMM YYYY HH:mm:ss") : rawRequestedDate;
      } else {
        requestedDateFormatted = dayjs().format("DD MMM YYYY HH:mm:ss");
      }

      if (isNewOrder) {
        orderedItem.labTestRequestedDate = requestedDateFormatted;
      }

      orderedLabTestsListPayload.push(orderedItem);
    });

    const payload = {
      enteredResultsList,
      orderedLabTestsList: orderedLabTestsListPayload
    };

    try {
      const response = await ncdAPI.submitInAssLabTestResults(individualId, payload);
      if (response) {
        toast.success("All lab investigations saved successfully!");
        setOrderedTests(prev => prev.map(t => ({ ...t, isLocalNewOrder: false })));
        await fetchOrderedTests();
        handleClose();
      } else {
        toast.error("Failed to save lab investigations");
      }
    } catch (error) {
      console.error("Save all error:", error);
      toast.error(error.message || "Failed to save lab investigations");
    }
  };

  // Check if "Order" button should be disabled
  const isOrderDisabled = selectedTests.length === 0 || (hasPendingWarning() && !warningChecked);

  return (
    <Card sx={{ 
      position: 'relative', 
      borderRadius: '16px', 
      boxShadow: '0px 8px 30px rgba(0, 0, 0, 0.06)', 
      border: '1px solid #eef2f5',
      overflow: 'visible',
      mb: 3,
      background: '#ffffff'
    }}>
      <CardContent sx={{ p: 4 }}>
        
        {/* Top Header Action Bar */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, pb: 2, borderBottom: '1px solid #eef2f5' }}>
          <Button 
            variant="contained" 
            onClick={() => {
              setSelectedTests([]);
              setWarningChecked(false);
              setShowOrderModal(true);
            }}
            sx={{ 
              bgcolor: '#3b51a3', 
              color: '#fff', 
              fontWeight: 700, 
              px: 3, 
              py: 1.2, 
              textTransform: 'none',
              borderRadius: '6px',
              boxShadow: '0 4px 12px rgba(59, 81, 163, 0.2)',
              '&:hover': { bgcolor: '#2e3f80', boxShadow: '0 6px 16px rgba(59, 81, 163, 0.3)' } 
            }}
          >
            ORDER TEST
          </Button>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Date"
                format="DD-MM-YYYY"
                value={dayjs(sessionDate)}
                onChange={(newValue) => setSessionDate(newValue ? newValue.format('YYYY-MM-DD') : today)}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { 
                      width: '180px', 
                      '& .MuiOutlinedInput-root': { borderRadius: '6px' } 
                    }
                  }
                }}
              />
            </LocalizationProvider>
          </Box>
        </Box>

        {/* Ordered Tests List Header */}
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#334155', mb: 3, fontSize: '16px' }}>
          Ordered Tests
        </Typography>

        {/* List of Cards */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
              <CircularProgress sx={{ color: '#3b51a3' }} />
            </Box>
          ) : (
            <>
              {orderedTests.map((test) => {
            const cardKey = test.uniqueCardKey || test.labTestOrderId || test.id;
            const isExpanded = expandedTestIds.includes(cardKey);
            const meta = TEST_METADATA[test.name] || { unit: "Not Applicable", range: "Normal", type: "text" };
            const isLocalNewOrder = Boolean(test.isLocalNewOrder || (typeof test.id === 'string' && test.id.startsWith('new_')));
            
            // Format today's date to match and compare
            const todayFormatted = dayjs(sessionDate).format("DD MMM YYYY");
            const isOrderedToday = test.orderedDate === todayFormatted || test.orderingFacility === "";

            const cardDateError = Boolean(dateErrors[cardKey]);
            const cardDateVal = tempResultDates[cardKey];

            return (
              <Card 
                key={cardKey} 
                variant="outlined" 
                sx={{ 
                  borderRadius: '10px', 
                  borderColor: isExpanded ? '#3b51a3' : '#e2e8f0',
                  boxShadow: isExpanded ? '0 4px 20px rgba(59, 81, 163, 0.08)' : 'none',
                  transition: 'all 0.25s ease'
                }}
              >
                <CardContent sx={{ p: 1.8, px: 2.5, '&:last-child': { pb: 1.8 } }}>
                  
                  {/* Top Status & Delete option */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ 
                        width: '8px', 
                        height: '8px', 
                        borderRadius: '50%', 
                        bgcolor: test.status === 'Completed' ? '#10b981' : '#ef4444' 
                      }} />
                      <Typography variant="caption" sx={{ 
                        color: test.status === 'Completed' ? '#059669' : '#ef4444', 
                        fontWeight: 700, 
                        fontSize: '12px',
                        textTransform: 'capitalize'
                      }}>
                        {test.status === 'Completed' ? 'Completed' : 'Pending'}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Test Title & Action */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '15px' }}>
                      {test.name}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {test.status === 'Completed' ? (
                        <Button
                          variant="outlined"
                          onClick={() => handleOpenEnterResult(test)}
                          sx={{
                            borderColor: '#3b51a3',
                            color: '#3b51a3',
                            fontWeight: 700,
                            textTransform: 'none',
                            px: 2,
                            py: 0.4,
                            borderRadius: '6px',
                            '&:hover': {
                              borderColor: '#2e3f80',
                              bgcolor: 'rgba(59, 81, 163, 0.04)'
                            }
                          }}
                        >
                          VIEW RESULT
                        </Button>
                      ) : isLocalNewOrder ? (
                        <IconButton 
                          onClick={() => handleDeleteTest(test.uniqueCardKey || test.id)} 
                          title="Delete Order"
                          sx={{ 
                            color: '#ef4444', 
                            p: 0.8,
                            border: '1px solid #fee2e2',
                            bgcolor: '#fef2f2',
                            borderRadius: '6px',
                            '&:hover': { bgcolor: '#fecdd3', borderColor: '#f87171' } 
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: '20px' }} />
                        </IconButton>
                      ) : (
                        <Button
                          variant="outlined"
                          onClick={() => handleOpenEnterResult(test)}
                          sx={{
                            borderColor: '#3b51a3',
                            color: '#3b51a3',
                            fontWeight: 700,
                            textTransform: 'none',
                            px: 2,
                            py: 0.4,
                            borderRadius: '6px',
                            '&:hover': {
                              borderColor: '#2e3f80',
                              bgcolor: 'rgba(59, 81, 163, 0.04)'
                            }
                          }}
                        >
                          ENTER RESULT
                        </Button>
                      )}
                    </Box>
                  </Box>

                  {/* Details row */}
                  <Grid container spacing={1} sx={{ alignItems: 'center', mt: 0.2 }}>
                    <Grid item xs={12} sm={test.status === 'Completed' ? 2 : 3.5}>
                      <Typography variant="caption" sx={{ display: 'block', color: '#94a3b8', fontWeight: 600, fontSize: '11.5px', mb: 0.2 }}>Ordered Date</Typography>
                      <Typography variant="body2" sx={{ color: '#334155', fontWeight: 500, fontSize: '13px' }}>{test.orderedDate}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={test.status === 'Completed' ? 2.5 : 4}>
                      <Typography variant="caption" sx={{ display: 'block', color: '#94a3b8', fontWeight: 600, fontSize: '11.5px', mb: 0.2 }}>Ordered by</Typography>
                      <Typography variant="body2" sx={{ color: '#334155', fontWeight: 500, fontSize: '13px' }}>{test.orderedBy}</Typography>
                    </Grid>
                    {test.status === 'Completed' && (
                      <Grid item xs={12} sm={2.5}>
                        <Typography variant="caption" sx={{ display: 'block', color: '#94a3b8', fontWeight: 600, fontSize: '11.5px', mb: 0.2 }}>Test Date</Typography>
                        <Typography variant="body2" sx={{ color: '#334155', fontWeight: 500, fontSize: '13px' }}>{test.resultDate || '-'}</Typography>
                      </Grid>
                    )}
                    <Grid item xs={12} sm={test.status === 'Completed' ? 2.5 : 4.5}>
                      <Typography variant="caption" sx={{ display: 'block', color: '#94a3b8', fontWeight: 600, fontSize: '11.5px', mb: 0.2 }}>Ordering Facility</Typography>
                      <Typography variant="body2" sx={{ color: '#334155', fontWeight: 500, fontSize: '13px' }}>{test.orderingFacility || '-'}</Typography>
                    </Grid>
                  </Grid>

                  {/* Expandable Result Form Section */}
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #cbd5e1', display: 'flex', flexDirection: 'column', gap: 1 }}>
                      
                      {/* Test Date selector */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.2, pr: 2 }}>
                        <Typography variant="body2" sx={{ color: '#64748b', fontSize: '12px' }}>
                          Choose a date
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#475569', fontSize: '13px' }}>
                            Test Date <span style={{ color: '#ef4444' }}>*</span>
                          </Typography>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                              value={cardDateVal ? dayjs(cardDateVal) : null}
                              onChange={(newValue) => {
                                setTempResultDates(prev => ({ ...prev, [cardKey]: newValue ? newValue.format('YYYY-MM-DD') : null }));
                                setDateErrors(prev => ({ ...prev, [cardKey]: false }));
                              }}
                              disabled={test.status === 'Completed'}
                              format="DD-MM-YYYY"
                              maxDate={dayjs()}
                              slotProps={{
                                textField: {
                                  variant: 'standard',
                                  error: cardDateError,
                                  helperText: cardDateError ? "Required" : "",
                                  InputProps: {
                                    disableUnderline: false,
                                    sx: {
                                      color: cardDateError ? '#ef4444' : '#64748b',
                                      fontWeight: 500,
                                      fontSize: '13px',
                                      '&:after': { borderBottom: cardDateError ? '2px solid #ef4444' : '1px dotted #cbd5e1' },
                                      '&:before': { borderBottom: cardDateError ? '2px solid #ef4444' : '1px dotted #cbd5e1' },
                                      '&.Mui-disabled:before': { borderBottom: '1px dotted #cbd5e1 !important' },
                                    }
                                  },
                                  sx: { 
                                    width: '120px',
                                  }
                                }
                              }}
                            />
                          </LocalizationProvider>
                        </Box>
                      </Box>

                      {/* Parameters Table */}
                      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '8px', overflow: 'hidden' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: '#e0f2f1' }}>
                              <TableCell sx={{ fontWeight: 700, color: '#334155', py: 0.8, px: 1.5, borderBottom: '1px solid #cbd5e1', fontSize: '13px' }}>Parameter</TableCell>
                              <TableCell sx={{ fontWeight: 700, color: '#334155', py: 0.8, px: 1.5, borderBottom: '1px solid #cbd5e1', fontSize: '13px' }}>Results</TableCell>
                              <TableCell sx={{ fontWeight: 700, color: '#334155', py: 0.8, px: 1.5, borderBottom: '1px solid #cbd5e1', fontSize: '13px' }}>Unit</TableCell>
                              <TableCell sx={{ fontWeight: 700, color: '#334155', py: 0.8, px: 1.5, borderBottom: '1px solid #cbd5e1', fontSize: '13px' }}>Reference Range</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(() => {
                              const effectiveChildren = getEffectiveChildReports(test);
                              const childDefs = CHILD_PARAMETER_DEFINITIONS[test.name];

                              if (effectiveChildren && effectiveChildren.length > 0) {
                                return effectiveChildren.map((param, idx) => {
                                  const childDef = childDefs ? childDefs[idx] : null;
                                  const paramName = (param.displayText && param.displayText !== test.name) 
                                    ? param.displayText 
                                    : (childDef?.name || param.displayText || test.name);

                                  const paramType = childDef?.type || (param.dataType === 'dropdown' ? 'dropdown' : (param.dataTypeValidCriteria === 'numeric' || param.dataTypeValidCriteria === 'numeric with decimal' ? 'numeric' : 'text'));
                                  const paramUnit = childDef?.unit || param.measurementUnit || "Not Applicable";
                                  const paramRange = childDef?.range || param.helpText || "Normal";
                                  const paramOptions = childDef?.options || (param.dataType === 'dropdown' && param.dataTypeValidCriteria ? param.dataTypeValidCriteria.split(',') : []);

                                  const paramKey = `${cardKey}_${param.id}`;
                                  const currentVal = tempResults[paramKey] !== undefined 
                                    ? tempResults[paramKey] 
                                    : (tempResults[param.id] !== undefined ? tempResults[param.id] : (localResults[paramKey] !== undefined ? localResults[paramKey] : (param.data || param.value || param.testValue || "")));

                                  return (
                                    <TableRow key={param.id || idx} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                      <TableCell sx={{ fontWeight: 600, color: '#475569', py: 0.8, px: 1.5, fontSize: '13px' }}>{paramName}</TableCell>
                                      
                                      <TableCell sx={{ py: 0.8, px: 1.5 }}>
                                        {paramType === 'dropdown' ? (
                                          <FormControl size="small" sx={{ width: '180px' }}>
                                            <Select
                                              value={currentVal}
                                              onChange={(e) => setTempResults(prev => ({ ...prev, [paramKey]: e.target.value, [param.id]: e.target.value }))}
                                              displayEmpty
                                              disabled={test.status === 'Completed'}
                                              sx={{ 
                                                borderRadius: '4px',
                                                bgcolor: test.status === 'Completed' ? '#f1f5f9' : '#fff',
                                                '& .MuiSelect-select': { py: 0.5, fontSize: '13px' },
                                                '&.Mui-disabled': {
                                                  '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#cbd5e1',
                                                  },
                                                  color: '#334155',
                                                  WebkitTextFillColor: '#334155',
                                                }
                                              }}
                                            >
                                              <MenuItem value="">Select</MenuItem>
                                              {paramOptions.map((opt) => (
                                                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                              ))}
                                            </Select>
                                          </FormControl>
                                        ) : (
                                          <TextField
                                            size="small"
                                            type={paramType === 'numeric' ? 'number' : 'text'}
                                            value={currentVal}
                                            placeholder="Enter value"
                                            onChange={(e) => setTempResults(prev => ({ ...prev, [paramKey]: e.target.value, [param.id]: e.target.value }))}
                                            disabled={test.status === 'Completed'}
                                            sx={{ 
                                              width: '180px', 
                                              '& .MuiOutlinedInput-root': { 
                                                borderRadius: '4px',
                                                bgcolor: test.status === 'Completed' ? '#f1f5f9' : '#fff',
                                                '& input': { py: 0.5, fontSize: '13px' },
                                                '&.Mui-disabled': {
                                                  '& fieldset': {
                                                    borderColor: '#cbd5e1',
                                                  },
                                                  '& input': {
                                                    color: '#334155',
                                                    fontWeight: 500,
                                                    WebkitTextFillColor: '#334155',
                                                  }
                                                }
                                              } 
                                            }}
                                          />
                                        )}
                                      </TableCell>
                                      
                                      <TableCell sx={{ color: '#475569', py: 0.8, px: 1.5, fontSize: '13px' }}>{paramUnit}</TableCell>
                                      <TableCell sx={{ color: '#475569', py: 0.8, px: 1.5, fontSize: '13px', whiteSpace: 'pre-line' }}>{formatReferenceRange(paramRange)}</TableCell>
                                    </TableRow>
                                  );
                                });
                              }

                              const singleVal = tempResults[cardKey] !== undefined ? tempResults[cardKey] : (localResults[cardKey] !== undefined ? localResults[cardKey] : (test.result || ""));

                              return (
                                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                  <TableCell sx={{ fontWeight: 600, color: '#475569', py: 0.8, px: 1.5, fontSize: '13px' }}>{test.name}</TableCell>
                                  <TableCell sx={{ py: 0.8, px: 1.5 }}>
                                    {meta.type === 'dropdown' ? (
                                      <FormControl size="small" sx={{ width: '180px' }}>
                                        <Select
                                          value={singleVal}
                                          onChange={(e) => setTempResults(prev => ({ ...prev, [cardKey]: e.target.value }))}
                                          displayEmpty
                                          disabled={test.status === 'Completed'}
                                          sx={{ 
                                            borderRadius: '4px',
                                            bgcolor: test.status === 'Completed' ? '#f1f5f9' : '#fff',
                                            '& .MuiSelect-select': { py: 0.5, fontSize: '13px' }
                                          }}
                                        >
                                          <MenuItem value="">Select</MenuItem>
                                          {meta.options.map((opt) => (
                                            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                          ))}
                                        </Select>
                                      </FormControl>
                                    ) : (
                                      <TextField
                                        size="small"
                                        type={meta.type === 'numeric' ? 'number' : 'text'}
                                        value={singleVal}
                                        placeholder="Enter value"
                                        onChange={(e) => setTempResults(prev => ({ ...prev, [cardKey]: e.target.value }))}
                                        disabled={test.status === 'Completed'}
                                        sx={{ 
                                          width: '180px', 
                                          '& .MuiOutlinedInput-root': { 
                                            borderRadius: '4px',
                                            bgcolor: test.status === 'Completed' ? '#f1f5f9' : '#fff',
                                            '& input': { py: 0.5, fontSize: '13px' } 
                                          } 
                                        }}
                                      />
                                    )}
                                  </TableCell>
                                  <TableCell sx={{ color: '#475569', py: 0.8, px: 1.5, fontSize: '13px' }}>{meta.unit}</TableCell>
                                  <TableCell sx={{ color: '#475569', py: 0.8, px: 1.5, fontSize: '13px', whiteSpace: 'pre-line' }}>{formatReferenceRange(meta.range)}</TableCell>
                                </TableRow>
                              );
                            })()}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      {/* File attachment preview */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5, px: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          {test.attachedLabTestFiles && test.attachedLabTestFiles.map((file, i) => (
                            <Box 
                              key={i} 
                              sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 0.5, 
                                bgcolor: '#f1f5f9', 
                                px: 1, 
                                py: 0.3, 
                                borderRadius: '4px',
                                border: '1px solid #cbd5e1'
                              }}
                            >
                              <Typography variant="caption" sx={{ color: '#475569', fontWeight: 500, fontSize: '11.5px' }}>
                                {file}
                              </Typography>
                              <IconButton 
                                size="small" 
                                onClick={() => handleRemoveAttachedFile(test.id, file)}
                                sx={{ p: 0.2, color: '#64748b', '&:hover': { color: '#ef4444' } }}
                              >
                                <CloseIcon sx={{ fontSize: '14px', fontWeight: 'bold' }} />
                              </IconButton>
                            </Box>
                          ))}
                        </Box>

                        {/* Attach File Button on the right */}
                        <Button 
                          component="label"
                          endIcon={<AttachFileIcon sx={{ fontSize: '16px', transform: 'rotate(45deg)' }} />}
                          sx={{ 
                            color: '#3b51a3', 
                            textTransform: 'none', 
                            fontWeight: 700,
                            fontSize: '13px',
                            py: 0.2,
                            '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } 
                          }}
                        >
                          Attach File
                          <input type="file" hidden onChange={(e) => handleAttachFile(test.id, e)} />
                        </Button>
                      </Box>

                      {/* Actions */}
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 0.5 }}>
                        {test.status !== 'Completed' && (
                          <Button 
                            onClick={() => setExpandedTestIds(prev => prev.filter(id => id !== cardKey))}
                            sx={{ 
                              color: '#00897b', 
                              fontWeight: 700, 
                              textTransform: 'none',
                              py: 0.4,
                              '&:hover': { bgcolor: 'rgba(0, 137, 123, 0.04)' }
                            }}
                          >
                            CANCEL
                          </Button>
                        )}
                        <Button 
                          variant="contained"
                          onClick={() => test.status === 'Completed' ? setExpandedTestIds(prev => prev.filter(id => id !== cardKey)) : handleSaveResult(cardKey)}
                          sx={{ 
                            bgcolor: '#00897b', 
                            color: '#fff', 
                            fontWeight: 700, 
                            textTransform: 'none',
                            px: 3.5,
                            py: 0.5,
                            borderRadius: '4px',
                            '&:hover': { bgcolor: '#00796b' }
                          }}
                        >
                          OK
                        </Button>
                      </Box>
                    </Box>
                  </Collapse>
                </CardContent>
              </Card>
            );
          })}

          {orderedTests.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4, border: '1px dashed #cbd5e1', borderRadius: '12px' }}>
              <Typography sx={{ color: '#64748b' }}>No tests ordered yet. Click "ORDER TEST" above to get started.</Typography>
            </Box>
          )}
            </>
          )}
        </Box>

        {/* Global Save Button */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, pt: 2, borderTop: '1px solid #eef2f5' }}>
          <Button 
            variant="contained" 
            onClick={handleSaveAll}
            sx={{ 
              bgcolor: '#3b51a3', 
              color: '#fff', 
              fontWeight: 700, 
              px: 4, 
              py: 1, 
              textTransform: 'none',
              borderRadius: '6px',
              boxShadow: '0 4px 12px rgba(59, 81, 163, 0.2)',
              '&:hover': { bgcolor: '#2e3f80' } 
            }}
          >
            SAVE
          </Button>
        </Box>

      </CardContent>

      {/* Floating Close Button */}
      <IconButton
        onClick={handleClose}
        sx={{
          border: "1px solid #eef2f5",
          width: "32px",
          height: "32px",
          bgcolor: "#fff",
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          cursor: "pointer",
          position: "absolute",
          right: "-12px",
          top: "-12px",
          zIndex: 10,
          "&:hover": {
            backgroundColor: "#f7fafc",
            color: '#e53e3e'
          },
        }}
      >
        <CloseIcon sx={{ fontSize: '16px' }} />
      </IconButton>

      {/* Order Test Modal Checklist dialog */}
      <Dialog 
        open={showOrderModal} 
        onClose={() => setShowOrderModal(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '16px', p: 1, boxShadow: '0 20px 40px rgba(0,0,0,0.12)' }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 800, 
          color: '#0f172a', 
          borderBottom: '1px solid #f1f5f9', 
          pb: 2, 
          fontSize: '20px',
          display: 'flex',
          justify: 'space-between',
          alignItems: 'center'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', fontSize: '20px' }}>
              Order Test
            </Typography>
            {selectedTests.length > 0 && (
              <Typography sx={{ bgcolor: '#e0e7ff', color: '#3730a3', fontSize: '13px', fontWeight: 700, px: 1.5, py: 0.4, borderRadius: '12px' }}>
                {selectedTests.length} Selected
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {selectedTests.length > 0 && (
              <Button 
                size="small" 
                onClick={() => setSelectedTests([])}
                sx={{ color: '#64748b', fontSize: '13px', fontWeight: 600, textTransform: 'none', '&:hover': { color: '#ef4444' } }}
              >
                Clear selection
              </Button>
            )}
            <IconButton 
              size="small" 
              onClick={() => setShowOrderModal(false)}
              sx={{ color: '#64748b', '&:hover': { color: '#0f172a' } }}
            >
              <CloseIcon sx={{ fontSize: '20px' }} />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ mt: 2, py: 2, px: 3 }}>
          <Box sx={{ 
            columnCount: { xs: 1, sm: 2, md: 3, lg: 4 }, 
            columnGap: 2.5,
            '& > div': {
              breakInside: 'avoid',
              marginBottom: 2.5
            }
          }}>
            {Object.entries(categoriesToRender).map(([categoryName, tests]) => {
              const selectedCountInCategory = tests.filter(t => selectedTests.includes(t.name)).length;
              return (
                <Paper
                  key={categoryName}
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: '12px',
                    bgcolor: selectedCountInCategory > 0 ? '#faf5ff' : '#f8fafc',
                    borderColor: selectedCountInCategory > 0 ? '#c084fc' : '#e2e8f0',
                    boxShadow: selectedCountInCategory > 0 ? '0 4px 12px rgba(192, 132, 252, 0.08)' : 'none',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, pb: 1, borderBottom: '2px solid #e2e8f0' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1e293b', fontSize: '14.5px' }}>
                      {categoryName}
                    </Typography>
                    <Typography variant="caption" sx={{ 
                      bgcolor: selectedCountInCategory > 0 ? '#9333ea' : '#e2e8f0', 
                      color: selectedCountInCategory > 0 ? '#ffffff' : '#475569', 
                      fontWeight: 700, 
                      px: 1, 
                      py: 0.2, 
                      borderRadius: '10px', 
                      fontSize: '11px' 
                    }}>
                      {selectedCountInCategory > 0 ? `${selectedCountInCategory}/${tests.length}` : tests.length}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {tests.map((test) => {
                      const isChecked = selectedTests.includes(test.name);
                      const isAlreadyPending = orderedTests.some(o => o.name === test.name && o.status === "Pending Results");
                      
                      return (
                        <Box 
                          key={test.name} 
                          onClick={() => handleCheckboxToggle(test.name)}
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            px: 1,
                            py: 0.4,
                            borderRadius: '6px',
                            bgcolor: isChecked ? '#eef2ff' : 'transparent',
                            cursor: 'pointer',
                            transition: 'background-color 0.15s ease',
                            '&:hover': {
                              bgcolor: isChecked ? '#e0e7ff' : '#f1f5f9'
                            }
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox 
                                checked={isChecked} 
                                onChange={() => handleCheckboxToggle(test.name)}
                                size="small"
                                sx={{ p: 0.5, color: '#94a3b8', '&.Mui-checked': { color: '#3b51a3' } }}
                              />
                            }
                            label={
                              <Typography sx={{ fontSize: '13px', color: isChecked ? '#1e1b4b' : '#334155', fontWeight: isChecked ? 600 : 500 }}>
                                {test.name}
                              </Typography>
                            }
                            sx={{ mr: 0, width: '100%', pointerEvents: 'none' }}
                          />
                          {test.warning && isAlreadyPending && (
                            <WarningIcon sx={{ color: '#ef4444', fontSize: '16px', ml: 0.5, flexShrink: 0 }} />
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Paper>
              );
            })}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2.5, borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {/* Bottom left warning checkbox */}
          <Box sx={{ maxWidth: '65%' }}>
            {hasPendingWarning() && (
              <Box sx={{ bgcolor: '#fff1f2', border: '1px solid #fecdd3', borderRadius: '8px', px: 2, py: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={warningChecked}
                      onChange={(e) => setWarningChecked(e.target.checked)}
                      size="small"
                      sx={{ color: '#f43f5e', '&.Mui-checked': { color: '#e11d48' } }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <WarningIcon sx={{ color: '#e11d48', fontSize: '16px', flexShrink: 0 }} />
                      <Typography sx={{ fontSize: '12px', color: '#be123c', fontWeight: 600 }}>
                        You have selected test/s that are already in Pending status. Click checkbox to proceed with re-ordering.
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            )}
          </Box>

          {/* Bottom right modal control buttons */}
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button 
              onClick={() => {
                setShowOrderModal(false);
                setSelectedTests([]);
                setWarningChecked(false);
              }}
              sx={{ 
                color: '#475569', 
                textTransform: 'none', 
                fontWeight: 700, 
                px: 3,
                borderRadius: '6px',
                '&:hover': { bgcolor: '#f1f5f9' }
              }}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              onClick={handleOrderTests}
              disabled={isOrderDisabled}
              sx={{ 
                bgcolor: '#3b51a3', 
                color: '#fff', 
                textTransform: 'none', 
                fontWeight: 700, 
                px: 3.5,
                py: 1,
                borderRadius: '6px',
                boxShadow: '0 4px 12px rgba(59, 81, 163, 0.25)',
                '&:hover': { bgcolor: '#2e3f80' },
                '&.Mui-disabled': { bgcolor: '#cbd5e1', color: '#94a3b8' }
              }}
            >
              Order {selectedTests.length > 0 ? `(${selectedTests.length})` : ''}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

    </Card>
  );
};
